<template>
  <div class="parties" v-if="getParties">
    <DataTable
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      :value="getParties"
      :loading="isLoading"
      :rowHover="true"
      :lazy="true"
      filterDisplay="row"
      :stateKey="tableStateStore.filterName()"
      :filters.sync="tableStateStore.filters"
      :rows.sync="tableStateStore.pagination.pageSize"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      :first="tableStateStore.skip()"
      stateStorage="local"
      :totalRecords="getPartyCount"
      :paginator="true"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 20, 50, 100]"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      responsiveLayout="scroll"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <h4 class="page-title">{{ $t('parties') }}</h4>
          </div>
          <div class="table-header-right">
            <button class="btn btn-sm btn-inverse" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>
            <button class="btn btn-sm btn-inverse" @click="$emit('create-party')">
              <i class="fa fa-plus-circle mr-2"></i>
              {{ $t('party') }} {{ $t('create') }}
            </button>
          </div>
        </div>
      </template>

      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <Column
        field="number"
        header="#"
        sortable
        :headerStyle="{ width: '60px' }"
        :bodyStyle="'text-align: center; width: 60px'"
        filterField="number"
      >
        <template #body="slotProps">
          <router-link
            v-if="slotProps.data && slotProps.data.number !== null"
            :to="{
              name: 'PartyEditPage',
              params: { partyNumber: slotProps.data.number },
            }"
          >
            {{ slotProps.data.number.toString().padStart(4, '0') }}
          </router-link>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="kind"
        :header="$t('kind')"
        sortable
        :headerStyle="{ width: '120px' }"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.kind">
            {{ $t(slotProps.data.kind) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.kindOption"
            :options="kindOptions"
            @input="onKindFilter"
            optionLabel="label"
            :placeholder="$t('kind')"
            display="chip"
            :style="{ 'max-width': '140px' }"
          >
            <template #option="slotProps">
              {{ $t(slotProps.option.value) }}
            </template>
          </MultiSelect>
        </template>
      </Column>

      <Column
        field="eligibleFor"
        :header="$t('eligibleFor')"
        :headerStyle="{ width: '180px' }"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span
            v-if="slotProps.data && slotProps.data.eligibleFor && slotProps.data.eligibleFor.length"
          >
            <ul>
              <li
                v-for="role in slotProps.data.eligibleFor"
                :key="role"
                class="mr-1 list-style-disc"
              >
                {{ $t(role) }}
              </li>
            </ul>
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.eligibleFor"
            :options="eligibleForOptions"
            @input="onEligibleForFilter"
            optionLabel="label"
            :placeholder="$t('eligibleFor')"
            display="chip"
            :style="{ 'max-width': '200px' }"
          >
            <template #option="slotProps">
              {{ $t(slotProps.option.value) }}
            </template>
          </MultiSelect>
        </template>
      </Column>

      <Column
        field="company"
        :header="$t('company')"
        sortable
        filterMatchMode="contains"
        filterField="partySearch"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.company">
            {{ slotProps.data.company }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('search')"
          />
        </template>
      </Column>

      <Column field="name" :header="$t('name')" filterField="nameSearch" :showFilterMenu="false">
        <template #body="slotProps">
          <span v-if="slotProps.data">
            {{ slotProps.data.firstname }} {{ slotProps.data.lastname }}<br />
            <small>{{ slotProps.data.email }}</small>
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('search')"
          />
        </template>
      </Column>

      <Column field="zip" :header="$t('zip')" sortable filterField="zip">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.zip">
            {{ slotProps.data.zip }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('search')"
          />
        </template>
      </Column>

      <Column field="city" :header="$t('city')" sortable filterField="city">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.city">
            {{ slotProps.data.city }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('search')"
          />
        </template>
      </Column>

      <Column
        field="createdBy"
        :header="$t('createdBy')"
        filterField="createdBy"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.createdBy">
            {{ slotProps.data.createdBy.firstname }} {{ slotProps.data.createdBy.lastname }}<br />
            <small>{{ slotProps.data.createdBy.email }}</small>
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('search')"
          />
        </template>
      </Column>

      <Column
        field="updatedBy"
        :header="$t('updatedBy')"
        filterField="updatedBy"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.updatedBy">
            {{ slotProps.data.updatedBy.firstname }} {{ slotProps.data.updatedBy.lastname }}<br />
            <small>{{ slotProps.data.updatedBy.email }}</small>
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('search')"
          />
        </template>
      </Column>

      <Column
        :header="$t('actions')"
        :headerStyle="{ width: '90px' }"
        :bodyStyle="'text-align: center; width: 90px'"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <button class="btn" @click="$emit('edit-party', slotProps.data)" :title="$t('edit')">
            <i class="link-icon fa fa-lg fa-edit"></i>
          </button>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';

export default {
  name: 'PartiesTable',
  components: {
    DataTable,
    InputText,
    Column,
    MultiSelect,
  },

  props: {
    tableStatePrefix: {
      type: String,
      default: 'parties',
    },
  },

  data() {
    return {
      partyTableStateConfig: {
        name: this.tableStatePrefix,
        filters: {
          number: { value: null },
          partySearch: { value: null },
          nameSearch: { value: null },
          zip: { value: null },
          city: { value: null },
          createdBy: { value: null },
          updatedBy: { value: null },
        },
        customFilters: {
          kindOption: null,
          eligibleFor: [],
        },
        sorting: { sortField: 'number', sortOrder: -1 },
      },
    };
  },

  computed: {
    ...mapGetters(['getParties', 'getPartyCount']),
    ...mapGetters(['isLoading', 'getTableState', 'getEnumValues']),

    tableStateStore() {
      return this.getTableState(this.partyTableStateConfig.name);
    },

    kindOptions() {
      const options = [];
      this.getEnumValues('PartyKind').forEach((value) => {
        options.push({ value, label: this.$t(value) });
      });
      return options;
    },

    eligibleForOptions() {
      const options = [];
      this.getEnumValues('PartyEligibleFor').forEach((value) => {
        options.push({ value, label: this.$t(value) });
      });
      return options;
    },
  },

  methods: {
    ...mapActions(['initTableState', 'fetchEnumValues', 'fetchPartiesPaginated']),

    async loadTableData() {
      const filters = { ...this.tableStateStore.filters, ...this.tableStateStore.customFilters };

      const apiFilters = {
        number: filters.number,
        partySearch: filters.partySearch,
        nameSearch: filters.nameSearch,
        zip: filters.zip,
        city: filters.city,
        createdBy: filters.createdBy,
        updatedBy: filters.updatedBy,
        kind: filters.kindOption ? { value: filters.kindOption.value } : undefined,
        eligibleFor:
          filters.eligibleFor && filters.eligibleFor.length
            ? filters.eligibleFor.map((o) => ({ value: o.value }))
            : undefined,
      };

      await this.fetchPartiesPaginated({
        pagination: {
          skip: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: apiFilters,
      });
    },

    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },

    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },

    async onFilter() {
      this.loadTableData();
    },

    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },

    async onKindFilter(value) {
      this.tableStateStore.setCustomFilters({ kindOption: value });
      await this.loadTableData();
    },

    async onEligibleForFilter(value) {
      this.tableStateStore.setCustomFilters({ eligibleFor: value || [] });
      await this.loadTableData();
    },

    async onRemoveFilters() {
      this.tableStateStore.reset();
      await this.loadTableData();
    },
  },

  async created() {
    await this.initTableState(this.partyTableStateConfig);
  },

  async mounted() {
    await this.fetchEnumValues('PartyKind');
    await this.fetchEnumValues('PartyEligibleFor');
    await this.loadTableData();
  },
};
</script>

<style lang="scss">
.p-column-filter-clear-button {
  display: none;
  width: 0;
}

.p-column-filter-menu-button {
  display: none;
  width: 0;
}

.table-header {
  display: flex;
  justify-content: space-between;
}

.table-header-left {
  display: flex;
  justify-content: flex-start;
}

.table-header-right {
  display: flex;
  justify-content: flex-end;
  column-gap: 1em;
}

/** Fix Dropdown overlay and table height */
.p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 37rem;
}
</style>
